import React, { useState, useEffect, useCallback } from 'react';
import { authFetch, getPushInfo, pushToProvider } from '../services/api';
import SecurityWarningModal from './SecurityWarningModal';

export default function GitPushModal({ projectId, projectName, onClose }) {
  const [pushInfo, setPushInfo] = useState(null);
  const [selectedProvider, setSelectedProvider] = useState(null); // "github" | "bitbucket"
  const [oauthToken, setOauthToken] = useState('');
  const [connecting, setConnecting] = useState(false);

  const [branchName, setBranchName] = useState(`feat/agent-${projectId.slice(0, 8)}`);
  const [repoName, setRepoName] = useState(projectName.replace(/[^a-zA-Z0-9_\-.]/g, '-').slice(0, 50));
  const [repoPrivate, setRepoPrivate] = useState(true);
  const [bbWorkspace, setBbWorkspace] = useState('');
  const [bbWorkspaces, setBbWorkspaces] = useState([]);   // [{slug, name}]
  const [bbWorkspacesLoading, setBbWorkspacesLoading] = useState(false);

  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [securityBlock, setSecurityBlock] = useState(null); // {findings: [...]}

  useEffect(() => {
    getPushInfo(projectId)
      .then(info => {
        setPushInfo(info);
        if (info.import_source === 'github') setSelectedProvider('github');
        else if (info.import_source === 'bitbucket') setSelectedProvider('bitbucket');
        // scratch: leave null so user picks
      })
      .catch(() => setPushInfo({ flow: 'new', import_source: 'scratch', source_git_url: '', github_repo_url: '' }));
  }, [projectId]);

  const handleOAuthResult = useCallback(async (data) => {
    if (data.status === 'error') { setError(`OAuth failed: ${data.payload}`); setConnecting(false); return; }
    try {
      const res = await authFetch(`/api/oauth/${data.provider}/poll?state=${data.payload}`);
      if (!res.ok) throw new Error('Failed to retrieve token');
      const json = await res.json();
      if (json.ready && json.token) {
      setOauthToken(json.token);
      if (data.provider === 'bitbucket') fetchBbWorkspaces(json.token);
    }
    } catch (e) { setError(e.message); }
    setConnecting(false);
  }, []);

  useEffect(() => {
    const onMessage = (e) => { if (e.data?.type === 'oauth_callback') handleOAuthResult(e.data); };
    const onStorage = (e) => {
      if (e.key !== 'oauth_result') return;
      try {
        const data = JSON.parse(e.newValue);
        if (data?.type === 'oauth_callback') { localStorage.removeItem('oauth_result'); handleOAuthResult(data); }
      } catch (_) {}
    };
    window.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('message', onMessage); window.removeEventListener('storage', onStorage); };
  }, [handleOAuthResult]);

  const fetchBbWorkspaces = async (token) => {
    setBbWorkspacesLoading(true);
    try {
      const res = await authFetch(`/api/oauth/bitbucket/workspaces?token=${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error('Failed to load workspaces');
      const data = await res.json();
      const list = data.workspaces || [];
      setBbWorkspaces(list);
      if (list.length === 1) setBbWorkspace(list[0].slug);
    } catch (e) { setError(e.message); }
    finally { setBbWorkspacesLoading(false); }
  };

  const connectProvider = async (provider) => {
    setError(''); setConnecting(true); setOauthToken('');
    setBbWorkspaces([]); setBbWorkspace('');
    setSelectedProvider(provider);
    try {
      const res = await authFetch(`/api/oauth/${provider}/authorize`);
      if (!res.ok) throw new Error('Failed to get auth URL');
      const { url } = await res.json();
      window.open(url, `oauth_${provider}_push_${Date.now()}`, 'width=600,height=700,left=300,top=100');
    } catch (e) { setError(e.message); setConnecting(false); }
  };

  const handlePush = async () => {
    if (!oauthToken) { setError('Connect an account first.'); return; }
    setError(''); setPushing(true);
    try {
      const payload = { token: oauthToken, branch_name: branchName.trim() };
      if (pushInfo?.flow === 'new') {
        payload.new_repo_name = repoName.trim();
        payload.new_repo_private = repoPrivate;
        payload.new_repo_description = `Generated by AI Code Agent — ${projectName}`;
        if (selectedProvider === 'bitbucket') payload.bitbucket_workspace = bbWorkspace.trim();
      }
      const data = await pushToProvider(projectId, payload);
      setResult(data);
    } catch (e) {
      // Check if it's a security block (403 with findings)
      try {
        const detail = JSON.parse(e.message);
        if (detail.security_blocked) { setSecurityBlock(detail); setPushing(false); return; }
      } catch (_) {}
      setError(e.message);
    }
    finally { setPushing(false); }
  };

  const isExisting = pushInfo?.flow === 'existing';
  const isScratch = pushInfo?.import_source === 'scratch';
  const canPush = oauthToken && !pushing && branchName.trim() &&
    (isExisting || (repoName.trim() && (selectedProvider !== 'bitbucket' || bbWorkspace.trim())));

  const providerLabel = selectedProvider === 'bitbucket' ? 'Bitbucket' : 'GitHub';
  const providerIcon = selectedProvider === 'bitbucket' ? '🪣' : '🐙';

  return (
    <>
    {securityBlock && (
      <SecurityWarningModal
        jobId={projectId}
        findings={securityBlock.findings || []}
        onAcknowledge={() => { setSecurityBlock(null); handlePush(); }}
        onClose={() => setSecurityBlock(null)}
      />
    )}
    <div style={s.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <span style={s.title}>🚀 Push to Git</span>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        {!pushInfo ? (
          <div style={s.center}>Loading project info...</div>
        ) : result ? (
          <div style={s.body}>
            <div style={s.successBox}>
              <span style={{ fontSize: 32 }}>✅</span>
              <div>
                <p style={s.successTitle}>Pushed successfully to {result.provider === 'bitbucket' ? 'Bitbucket' : 'GitHub'}!</p>
                <p style={s.successSub}>Branch: <code style={s.code}>{result.branch}</code> · Commit: <code style={s.code}>{result.commit_sha}</code></p>
              </div>
            </div>
            <div style={s.linkRow}>
              <a href={result.repo_url} target="_blank" rel="noreferrer" style={s.link}>View Repository</a>
              {result.pr_url && <a href={result.pr_url} target="_blank" rel="noreferrer" style={s.link}>View Pull Request</a>}
            </div>
            <button onClick={onClose} style={s.primaryBtn}>Done</button>
          </div>
        ) : (
          <div style={s.body}>
            {/* Flow badge */}
            <div style={s.flowBadge}>
              {isExisting
                ? <><span style={s.dot}>●</span> Pushing to: <code style={s.code}>{pushInfo.source_git_url.replace(/https?:\/\//, '').replace(/\.git$/, '')}</code></>
                : <><span style={{ ...s.dot, color: '#f59e0b' }}>●</span> Creating a new repository</>}
            </div>

            {/* Provider selector — only for scratch projects */}
            {isScratch && (
              <div style={s.section}>
                <label style={s.label}>Push to</label>
                <div style={s.toggleRow}>
                  {[{ id: 'github', icon: '🐙', label: 'GitHub' }, { id: 'bitbucket', icon: '🪣', label: 'Bitbucket' }].map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedProvider(p.id); setOauthToken(''); setError(''); }}
                      style={{ ...s.toggleBtn, ...(selectedProvider === p.id ? s.toggleBtnActive : {}) }}
                    >
                      {p.icon} {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* OAuth connect */}
            {selectedProvider && (
              <div style={s.section}>
                <label style={s.label}>{providerIcon} {providerLabel} Account</label>
                {oauthToken ? (
                  <div style={s.connectedRow}>
                    <span style={s.connectedText}>✅ Connected</span>
                    <button onClick={() => setOauthToken('')} style={s.disconnectBtn}>Disconnect</button>
                  </div>
                ) : (
                  <button onClick={() => connectProvider(selectedProvider)} disabled={connecting} style={{ ...s.oauthBtn, ...(connecting ? s.disabled : {}) }}>
                    {connecting ? 'Waiting for authorization...' : `Connect ${providerLabel}`}
                  </button>
                )}
              </div>
            )}

            {/* Branch name */}
            <div style={s.section}>
              <label style={s.label}>Branch Name</label>
              <input style={s.input} value={branchName} onChange={e => setBranchName(e.target.value)} placeholder="feat/agent-abc12345" />
            </div>

            {/* Flow 2: new repo settings */}
            {!isExisting && selectedProvider && (
              <>
                <div style={s.section}>
                  <label style={s.label}>Repository Name</label>
                  <input style={s.input} value={repoName} onChange={e => setRepoName(e.target.value)} placeholder="my-project" />
                </div>
                {selectedProvider === 'bitbucket' && (
                  <div style={s.section}>
                    <label style={s.label}>Bitbucket Workspace</label>
                    {bbWorkspacesLoading ? (
                      <div style={s.wsLoading}>Loading workspaces...</div>
                    ) : bbWorkspaces.length > 0 ? (
                      <select
                        style={s.select}
                        value={bbWorkspace}
                        onChange={e => setBbWorkspace(e.target.value)}
                      >
                        <option value="">— Select a workspace —</option>
                        {bbWorkspaces.map(ws => (
                          <option key={ws.slug} value={ws.slug}>{ws.name} ({ws.slug})</option>
                        ))}
                      </select>
                    ) : (
                      <input style={s.input} value={bbWorkspace} onChange={e => setBbWorkspace(e.target.value)} placeholder="your-workspace-slug" />
                    )}
                  </div>
                )}
                <div style={s.section}>
                  <label style={s.label}>Visibility</label>
                  <div style={s.toggleRow}>
                    {[{ val: true, label: '🔒 Private' }, { val: false, label: '🌐 Public' }].map(({ val, label }) => (
                      <button key={String(val)} onClick={() => setRepoPrivate(val)}
                        style={{ ...s.toggleBtn, ...(repoPrivate === val ? s.toggleBtnActive : {}) }}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {error && <p style={s.error}>⚠ {error}</p>}

            <div style={s.actions}>
              <button onClick={handlePush} disabled={!canPush} style={{ ...s.primaryBtn, ...(!canPush ? s.disabled : {}) }}>
                {pushing ? '⏳ Pushing...' : isExisting ? '🚀 Push Branch' : '🚀 Create Repo & Push'}
              </button>
              <button onClick={onClose} style={s.secondaryBtn}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
}

const s = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: '100%', maxWidth: 520, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' },
  title: { fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' },
  closeBtn: { background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1 },
  body: { display: 'flex', flexDirection: 'column', gap: 16, padding: 24 },
  center: { padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 },
  flowBadge: { fontSize: 12, color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  dot: { color: '#22c55e', fontSize: 8 },
  section: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' },
  input: { padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  oauthBtn: { padding: '10px 18px', background: '#24292e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', width: 'fit-content' },
  connectedRow: { display: 'flex', alignItems: 'center', gap: 12 },
  connectedText: { fontSize: 13, color: '#22c55e', fontWeight: 600 },
  disconnectBtn: { fontSize: 12, color: 'var(--text-muted)', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' },
  toggleRow: { display: 'flex', gap: 8 },
  toggleBtn: { padding: '8px 16px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', fontWeight: 500 },
  toggleBtnActive: { borderColor: '#6366f1', color: '#a5b4fc', background: 'rgba(99,102,241,0.12)' },
  error: { fontSize: 13, color: '#f87171', background: 'rgba(248,113,113,0.08)', padding: '10px 14px', borderRadius: 8, margin: 0 },
  actions: { display: 'flex', gap: 10 },
  primaryBtn: { padding: '11px 22px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  secondaryBtn: { padding: '11px 22px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  disabled: { opacity: 0.45, cursor: 'not-allowed' },
  successBox: { display: 'flex', alignItems: 'flex-start', gap: 16, padding: '16px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 10 },
  successTitle: { fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 4px' },
  successSub: { fontSize: 12, color: 'var(--text-muted)', margin: 0 },
  linkRow: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  link: { fontSize: 13, color: '#a5b4fc', textDecoration: 'none', padding: '8px 14px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, fontWeight: 600 },
  code: { fontFamily: 'monospace', fontSize: 11, background: 'rgba(255,255,255,0.07)', padding: '1px 5px', borderRadius: 4 },
  select: { padding: '10px 12px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', cursor: 'pointer' },
  wsLoading: { fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' },
};
